import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Plane, LogOut, ExternalLink, MapPin, Gauge, Clock,
  Award, Loader2, Ticket, PlaneTakeoff, Building2, TrendingUp,
} from "lucide-react";
import {
  getMe, getMyBids, getMyFleet, getRanks, isAuthed, clearSession,
  getStoredUser, SITE_DASHBOARD_URL,
} from "@/lib/api";
import { formatMinutes, initials } from "@/lib/format";
import { Epaulette } from "@/components/ranks/Epaulette";

const StatCard = ({ icon: Icon, label, value, index }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4, delay: index * 0.06 }}
    data-testid={`dash-stat-${label.toLowerCase().replace(/\s/g, "-")}`}
    className="rounded-2xl border border-white/5 bg-[#12141D] p-5"
  >
    <span className="flex h-10 w-10 items-center justify-center rounded-full border border-[#D4AF37]/25 bg-[#D4AF37]/5">
      <Icon className="h-5 w-5 text-[#D4AF37]" />
    </span>
    <p className="mt-4 font-mono-data text-2xl font-bold text-[#FDFBF7]">{value}</p>
    <p className="text-overline mt-1 text-[#A1A1AA]">{label}</p>
  </motion.div>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [user, setUser] = useState(getStoredUser());
  const [bids, setBids] = useState([]);
  const [fleet, setFleet] = useState([]);
  const [ranks, setRanks] = useState([]);
  const [loading, setLoading] = useState(true);

  const logout = useCallback(() => {
    clearSession();
    toast.success("Signed out.");
    navigate("/login");
  }, [navigate]);

  useEffect(() => {
    if (!isAuthed()) {
      navigate("/login");
      return;
    }
    let active = true;
    (async () => {
      try {
        const me = await getMe();
        if (active) setUser(me);
        const [b, f, rk] = await Promise.allSettled([getMyBids(), getMyFleet(), getRanks()]);
        if (active && b.status === "fulfilled") setBids(b.value || []);
        if (active && f.status === "fulfilled") setFleet(f.value || []);
        if (active && rk.status === "fulfilled") setRanks(rk.value || []);
      } catch (err) {
        if (err?.response?.status === 401) {
          clearSession();
          toast.error("Session expired. Please sign in again.");
          navigate("/login");
        } else {
          toast.error("Could not load your dashboard data.");
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [navigate]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#0A0B10]" data-testid="dashboard-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#8A1538]" />
      </div>
    );
  }

  const aircraft = fleet.flatMap((sf) =>
    (sf.aircraft || []).map((ac) => ({ ...ac, subfleet: sf.name, type: sf.type }))
  );

  // Rank progression based on total block hours vs the rank ladder.
  const pilotHours = (user?.total_time ?? user?.flight_time ?? 0) / 60;
  let curIdx = 0;
  ranks.forEach((r, i) => {
    if (pilotHours >= (r.hours || 0)) curIdx = i;
  });
  const currentRank = ranks[curIdx];
  const nextRank = ranks[curIdx + 1];
  const progress = nextRank
    ? Math.min(100, Math.max(0, ((pilotHours - currentRank.hours) / (nextRank.hours - currentRank.hours)) * 100))
    : 100;
  const hoursToNext = nextRank ? Math.max(0, nextRank.hours - pilotHours) : 0;

  return (
    <div className="min-h-screen bg-[#0A0B10]" data-testid="dashboard-page">
      {/* Top bar */}
      <header className="glass sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 lg:px-8">
          <Link to="/" className="flex items-center gap-3" data-testid="dashboard-logo">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#8A1538]">
              <Plane className="h-4 w-4 -rotate-45 text-[#D4AF37]" />
            </span>
            <span className="font-display text-base font-bold text-[#FDFBF7]">Air India Virtual</span>
          </Link>
          <div className="flex items-center gap-3">
            <a
              href={SITE_DASHBOARD_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="dashboard-fullsite-btn"
              className="hidden items-center gap-1.5 rounded-full border border-white/15 px-4 py-2 text-xs font-semibold text-[#FDFBF7] transition-colors hover:border-[#D4AF37]/50 sm:inline-flex"
            >
              Full Dashboard <ExternalLink className="h-3.5 w-3.5" />
            </a>
            <button
              onClick={logout}
              data-testid="dashboard-logout-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#8A1538] px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-[#A81A45]"
            >
              <LogOut className="h-3.5 w-3.5" /> Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-5 py-10 lg:px-8">
        {/* Profile */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col items-start gap-6 rounded-2xl border border-white/5 bg-[#12141D] p-7 sm:flex-row sm:items-center"
          data-testid="dashboard-profile"
        >
          {user?.avatar ? (
            <img
              src={user.avatar}
              alt={user?.name || "Pilot"}
              className="h-20 w-20 rounded-full border border-[#D4AF37]/40 object-cover"
            />
          ) : (
            <span className="flex h-20 w-20 items-center justify-center rounded-full border border-[#D4AF37]/40 bg-[#8A1538]/20 font-display text-2xl font-bold text-[#D4AF37]">
              {initials(user?.name) || <Plane className="h-7 w-7" />}
            </span>
          )}
          <div className="flex-1">
            <p className="text-overline text-[#8A1538]">{user?.rank?.name || "Pilot"}</p>
            <h1 className="mt-1 font-display text-3xl font-bold tracking-tight text-[#FDFBF7]">
              {user?.name || "Pilot"}
            </h1>
            <div className="mt-2 flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-[#A1A1AA]">
              {user?.ident && <span className="font-mono-data text-[#D4AF37]">{user.ident}</span>}
              {user?.airline?.name && (
                <span className="inline-flex items-center gap-1.5">
                  <Building2 className="h-4 w-4 text-[#D4AF37]" /> {user.airline.name}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="h-4 w-4 text-[#D4AF37]" /> {user?.curr_airport || user?.home_airport || "—"}
              </span>
            </div>
          </div>
          {currentRank && (
            <div className="hidden flex-col items-center gap-2 sm:flex" data-testid="dashboard-rank-badge">
              <Epaulette stripes={currentRank.stripes} wings={currentRank.wings} stars={currentRank.stars} />
              <span className="text-[11px] font-semibold text-[#D4AF37]">{currentRank.name}</span>
            </div>
          )}
        </motion.div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
          <StatCard icon={PlaneTakeoff} label="Flights" value={(user?.flights ?? 0).toLocaleString()} index={0} />
          <StatCard icon={Clock} label="Flight Hours" value={formatMinutes(user?.flight_time)} index={1} />
          <StatCard icon={Award} label="Total Hours" value={formatMinutes(user?.total_time ?? user?.flight_time)} index={2} />
          <StatCard icon={Gauge} label="Rank" value={user?.rank?.name || "—"} index={3} />
        </div>

        {/* Home / Current airport row */}
        <div className="mt-4 grid grid-cols-2 gap-4">
          <StatCard icon={MapPin} label="Home Base" value={user?.home_airport || "—"} index={4} />
          <StatCard icon={Gauge} label="Current" value={user?.curr_airport || "—"} index={5} />
        </div>

        {/* Rank progression */}
        {currentRank && (
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4, delay: 0.3 }}
            data-testid="dashboard-rank-progress"
            className="mt-4 rounded-2xl border border-white/5 bg-[#12141D] p-6"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-[#D4AF37]" />
                <p className="text-overline text-[#A1A1AA]">Rank Progress</p>
              </div>
              <span className="font-mono-data text-xs text-[#A1A1AA]">
                {Math.round(pilotHours).toLocaleString()}h logged
              </span>
            </div>

            {nextRank ? (
              <>
                <div className="mt-4 flex items-center justify-between text-sm">
                  <span className="font-semibold text-[#FDFBF7]">{currentRank.name}</span>
                  <span className="font-semibold text-[#D4AF37]">{nextRank.name}</span>
                </div>
                <div className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-white/10">
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: `${progress}%` }}
                    transition={{ duration: 1, ease: "easeOut" }}
                    data-testid="rank-progress-bar"
                    className="h-full rounded-full bg-gradient-to-r from-[#8A1538] to-[#D4AF37]"
                  />
                </div>
                <p className="mt-2 text-xs text-[#A1A1AA]">
                  <span className="font-mono-data font-semibold text-[#D4AF37]">
                    {Math.round(hoursToNext).toLocaleString()}h
                  </span>{" "}
                  to reach {nextRank.name}
                </p>
              </>
            ) : (
              <p className="mt-3 text-sm font-semibold text-[#D4AF37]">
                Top rank achieved — {currentRank.name}. Nothing left to prove!
              </p>
            )}
          </motion.div>
        )}

        <div className="mt-10 grid gap-8 lg:grid-cols-2">
          {/* Booked flights (bids) */}
          <section data-testid="dashboard-bids">
            <div className="mb-4 flex items-center gap-2">
              <Ticket className="h-5 w-5 text-[#D4AF37]" />
              <h2 className="font-display text-xl font-bold text-[#FDFBF7]">Booked Flights</h2>
            </div>
            {bids.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-[#A1A1AA]">
                No booked flights yet. Book a bid on the main portal.
              </div>
            ) : (
              <div className="space-y-3">
                {bids.map((b) => (
                  <div
                    key={b.id}
                    data-testid={`bid-${b.id}`}
                    className="flex items-center justify-between rounded-xl border border-white/5 bg-[#12141D] px-4 py-3.5"
                  >
                    <div>
                      <p className="font-mono-data text-sm font-bold text-[#FDFBF7]">
                        {b.airline?.icao || ""}{b.flight_number}
                      </p>
                      <p className="mt-0.5 flex items-center gap-2 text-sm text-[#A1A1AA]">
                        <span>{b.dpt_airport_id}</span>
                        <Plane className="h-3.5 w-3.5 -rotate-45 text-[#8A1538]" />
                        <span>{b.arr_airport_id}</span>
                      </p>
                    </div>
                    <span className="font-mono-data text-xs text-[#D4AF37]">
                      {formatMinutes(b.flight_time)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>

          {/* Fleet access */}
          <section data-testid="dashboard-fleet">
            <div className="mb-4 flex items-center gap-2">
              <Plane className="h-5 w-5 text-[#D4AF37]" />
              <h2 className="font-display text-xl font-bold text-[#FDFBF7]">Your Fleet Access</h2>
            </div>
            {aircraft.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-white/10 p-8 text-center text-sm text-[#A1A1AA]">
                No aircraft assigned to your rank yet.
              </div>
            ) : (
              <div className="space-y-3">
                {aircraft.slice(0, 8).map((ac) => (
                  <div
                    key={ac.id}
                    data-testid={`aircraft-${ac.id}`}
                    className="flex items-center justify-between rounded-xl border border-white/5 bg-[#12141D] px-4 py-3.5"
                  >
                    <div>
                      <p className="text-sm font-semibold text-[#FDFBF7]">{ac.name || ac.subfleet}</p>
                      <p className="font-mono-data text-xs text-[#A1A1AA]">{ac.registration || ac.type}</p>
                    </div>
                    <span className="rounded-full border border-white/10 px-3 py-1 font-mono-data text-xs text-[#D4AF37]">
                      {ac.icao || ac.type}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </main>
    </div>
  );
}
